<template>
  <div
    class="bg-white rounded-lg overflow-hidden shadow-md mx-auto relative flex flex-col max-h-[85dvh]"
    :class="backdate ? 'max-w-lg' : 'max-w-2xl'"
  >
    <div
      class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full"
    >
      <svg
        width="20"
        height="9"
        viewBox="0 0 20 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M10 1L18 9L10 9L2 9L10 1Z" fill="white" />
        <path
          d="M10 3.10701L15.3929 8.49991L10 8.4999L4.60711 8.4999L10 3.10701Z"
          stroke="white"
        />
      </svg>
    </div>
    <div class="flex flex-col md:flex-row gap-6 min-h-0 overflow-y-auto p-5">
      <div class="shrink-0">
        <div class="flex justify-between items-center mb-4">
          <button
            type="button"
            class="inline-flex items-center p-1 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-full transition-colors duration-200 cursor-pointer"
            @click="previousMonth"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.8572 6.81378H3.90239L6.52282 4.06273C6.63197 3.95206 6.71903 3.81967 6.77892 3.6733C6.83881 3.52692 6.87034 3.36949 6.87166 3.21019C6.87298 3.05088 6.84406 2.8929 6.7866 2.74545C6.72914 2.59801 6.64428 2.46405 6.53698 2.3514C6.42968 2.23875 6.30209 2.14967 6.16164 2.08934C6.0212 2.02902 5.87072 1.99866 5.71898 2.00005C5.56724 2.00143 5.41728 2.03453 5.27786 2.0974C5.13843 2.16028 5.01233 2.25168 4.90691 2.36627L0.335739 7.16531C0.229315 7.27676 0.144878 7.40916 0.0872666 7.55491C0.0296551 7.70067 0 7.85693 0 8.01474C0 8.17255 0.0296551 8.32881 0.0872666 8.47457C0.144878 8.62033 0.229315 8.75273 0.335739 8.86417L4.90691 13.6632C5.12244 13.8818 5.41111 14.0027 5.71075 14C6.01039 13.9972 6.29702 13.871 6.5089 13.6486C6.72078 13.4262 6.84097 13.1252 6.84357 12.8107C6.84618 12.4961 6.73099 12.193 6.52282 11.9668L3.90239 9.2133H14.8572C15.1603 9.2133 15.451 9.0869 15.6653 8.8619C15.8796 8.6369 16 8.33174 16 8.01354C16 7.69535 15.8796 7.39018 15.6653 7.16519C15.451 6.94019 15.1603 6.81378 14.8572 6.81378Z"
                fill="#111827"
              />
            </svg>
          </button>
          <h2 class="text-lg font-semibold text-gray-900">
            {{ monthYearDisplay }}
          </h2>
          <button
            type="button"
            class="inline-flex items-center p-1 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-full transition-colors duration-200 cursor-pointer"
            @click="nextMonth"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.14284 9.18622H12.0976L9.47718 11.9373C9.36803 12.0479 9.28097 12.1803 9.22108 12.3267C9.16119 12.4731 9.12966 12.6305 9.12834 12.7898C9.12702 12.9491 9.15594 13.1071 9.2134 13.2546C9.27086 13.402 9.35572 13.536 9.46302 13.6486C9.57032 13.7613 9.69791 13.8503 9.83836 13.9107C9.9788 13.971 10.1293 14.0013 10.281 14C10.4328 13.9986 10.5827 13.9655 10.7221 13.9026C10.8616 13.8397 10.9877 13.7483 11.0931 13.6337L15.6643 8.83469C15.7707 8.72324 15.8551 8.59084 15.9127 8.44509C15.9703 8.29933 16 8.14307 16 7.98526C16 7.82745 15.9703 7.67119 15.9127 7.52543C15.8551 7.37967 15.7707 7.24727 15.6643 7.13583L11.0931 2.33683C10.8776 2.11822 10.5889 1.99731 10.2893 2.00005C9.98961 2.00279 9.70298 2.12896 9.4911 2.35137C9.27922 2.57379 9.15903 2.87479 9.15643 3.18935C9.15382 3.50391 9.26901 3.80705 9.47718 4.03317L12.0976 6.7867H1.14284C0.839731 6.7867 0.549006 6.9131 0.334724 7.1381C0.120442 7.3631 0 7.66826 0 7.98646C0 8.30465 0.120442 8.60982 0.334724 8.83481C0.549006 9.05981 0.839731 9.18622 1.14284 9.18622Z"
                fill="#111827"
              />
            </svg>
          </button>
        </div>
        <div class="grid grid-cols-7 gap-2 mb-2">
          <div
            v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
            :key="day"
            class="text-center text-gray-500 text-sm font-medium"
          >
            {{ day }}
          </div>
        </div>
        <div class="grid grid-cols-7 gap-2">
          <button
            v-for="day in calendarDays"
            :key="day.date.toISOString()"
            :data-cy="
              day.isCurrentMonth ? `cal-day-${day.dayNumber}` : undefined
            "
            class="h-8 w-8 font-extrabold flex items-center justify-center rounded-lg text-sm transition-colors duration-200 focus:outline-none cursor-pointer"
            :class="{
              'text-gray-400': !day.isCurrentMonth || day.isDisabled,
              'bg-blue-600 text-white hover:bg-blue-700': day.isSelected,
              'hover:bg-gray-100 text-gray-900':
                !day.isSelected && day.isCurrentMonth && !day.isDisabled,
              'border border-blue-500': day.isToday && !day.isSelected,
              'text-blue-500': day.isToday && !day.isSelected,
              '=pointer-events-none': day.isDisabled
            }"
            :disabled="day.isDisabled"
            @click="() => !day.isDisabled && selectDate(day.date)"
          >
            {{ day.dayNumber }}
          </button>
        </div>
      </div>
      <div class="flex-1 min-w-0">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">
          Appointment Time
        </h2>
        <div v-if="backdate" class="space-y-3">
          <TimeInput v-model="manualTime" />
          <Checkbox v-model="syncTechTimes">
            <span class="text-xs text-gray-500">
              Sync technician times to the new date
            </span>
          </Checkbox>
        </div>
        <div v-else class="grid grid-cols-3 gap-2 overflow-scroll max-h-56">
          <button
            v-for="timeSlot in timeSlots"
            :key="timeSlot.scheduled_time"
            type="button"
            data-cy="time-slot-btn"
            class="py-2 px-3 text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-200 transition-colors duration-200 rounded-lg whitespace-nowrap"
            :class="{
              // Status 1 styling: Gray (DISABLED - HIGHEST PRIORITY)
              'bg-gray-100 text-gray-300 disabled': isPast(
                timeSlot.scheduled_time
              ),
              // Selected styling (OVERRIDE) - SECOND HIGHEST PRIORITY
              'bg-yellow-300 text-white cursor-pointer':
                selectedTimeSlot?.scheduled_time === timeSlot.scheduled_time &&
                timeSlot.disabled,

              // Selected styling (NORMAL) - THIRD PRIORITY
              'bg-green-400 text-white cursor-pointer':
                selectedTimeSlot?.scheduled_time === timeSlot.scheduled_time &&
                !timeSlot.disabled,

              // Status 2 styling: Yellow/Amber (OVERRIDE - NOT SELECTED) - FOURTH PRIORITY
              'bg-yellow-100 hover:bg-yellow-300 text-yellow-500 hover:text-white cursor-pointer':
                !isPast(timeSlot.scheduled_time) &&
                timeSlot.disabled &&
                selectedTimeSlot?.scheduled_time !== timeSlot.scheduled_time,

              // Default styling (available) - LOWEST PRIORITY
              'text-green-600 bg-green-200 hover:bg-green-400 hover:text-white cursor-pointer':
                !timeSlot.disabled &&
                selectedTimeSlot?.scheduled_time !== timeSlot.scheduled_time
            }"
            :disabled="isPast(timeSlot.scheduled_time)"
            @click="selectTimeSlot(timeSlot)"
          >
            {{ getTimeSlotLabel(timeSlot) }}
          </button>
        </div>
      </div>
    </div>

    <div class="shrink-0 border-t border-gray-100 bg-white px-5 pt-4 pb-5">
      <Toggle
        v-if="showNotifyToggle && !backdate"
        v-model:checked="notifyCustomer"
        label-after="Notify customer of this appointment"
        data-cy="notify-customer-toggle"
        class="mb-4"
      />
      <Button
        class="w-full"
        data-cy="reschedule-btn"
        :label="backdate ? 'Save' : 'Reschedule'"
        @click="handleSave"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, onMounted, ref, watch } from "vue";
import { currentTenantTimezone } from "@/lib/helpers";

import Button from "@/components/Seranova/Buttons/Button.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import TimeInput from "@/components/Seranova/TimeInput/TimeInput.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import { useAppointmentAvailability } from "@/AppointmentCreateModal/useAppointmentAvailability";
import { AppointmentV2 } from "@/typings/appointment";

const emit = defineEmits<{
  "request-save": [
    data: {
      action: string;
      payload: Record<string, unknown>;
    }
  ];
}>();
const {
  disabledDates,
  timeSlots,
  getTimeSlotLabel,
  selectTimeSlot,
  selectedTimeSlot,
  isPast,
  getMonthAvailability,
  getDateTimeSlots
} = useAppointmentAvailability();
// Props
const props = withDefaults(
  defineProps<{
    appointment: AppointmentV2;
    backdate?: boolean;
    showNotifyToggle?: boolean;
  }>(),
  { backdate: false, showNotifyToggle: false }
);

// Default ON: the customer expecting the old slot is the one who needs
// telling about the change, so the safer default is to notify them.
const notifyCustomer = ref(true);

const techUserIds = computed(() => {
  const techs = props.appointment.technicians;
  if (!techs?.length) return undefined;
  const ids = techs.map((t) => t.user_id);
  return ids.length > 1 ? ids : undefined;
});
interface Day {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isDisabled: boolean;
}

// State
const currentMonthDate = ref(new Date()); // Initialize with today's date
const selectedDate = ref<Date | null>(new Date()); // Initialize with today's date
const calendarDays = ref<Day[]>([]);

// In backdate mode the availability grid is replaced by a free time entry,
// seeded from the appointment's current scheduled time-of-day.
const manualTime = ref(
  moment
    .tz(props.appointment.scheduled_time, currentTenantTimezone)
    .format("h:mm A")
);

// Backdate default: also shift the technicians' actual-time dates to the new
// scheduled date so the admin doesn't have to backdate each tech by hand.
const syncTechTimes = ref(true);

// Computed values
const monthYearDisplay = computed(() => {
  const options = { month: "long", year: "numeric" };
  return currentMonthDate.value.toLocaleDateString("en-US", options);
});

// Methods
const generateCalendar = () => {
  const year = currentMonthDate.value.getFullYear();
  const month = currentMonthDate.value.getMonth();

  // Get first day of month and last day of month
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Get the day of the week for the first day (0 = Sunday, 6 = Saturday)
  const firstDayOfWeek = firstDay.getDay();

  // Generate calendar days
  const days: Day[] = [];

  // Add days from previous month
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = 0; i < firstDayOfWeek; i++) {
    const date = new Date(
      year,
      month - 1,
      prevMonthLastDay - firstDayOfWeek + i + 1
    );
    days.push({
      date: date,
      dayNumber: prevMonthLastDay - firstDayOfWeek + i + 1,
      isCurrentMonth: false,
      isSelected: isSameDay(date, selectedDate.value),
      isToday: isSameDay(date, new Date()),
      isDisabled:
        !props.backdate &&
        (date < new Date() || disabledDates.value.includes(date))
    });
  }

  // Add days from current month
  const today = new Date();
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    days.push({
      date: date,
      dayNumber: day,
      isCurrentMonth: true,
      isSelected: isSameDay(date, selectedDate.value),
      isToday: isSameDay(date, today),
      isDisabled:
        !props.backdate &&
        (date < new Date().setDate(new Date().getDate() - 1) ||
          disabledDates.value.includes(date))
    });
  }

  // Calculate how many rows we need (max 5 rows = 35 days)
  const maxDaysToShow = 35; // 5 weeks
  const daysNeeded = maxDaysToShow - days.length;

  // Add days from next month (only enough to fill 5 rows)
  for (let day = 1; day <= daysNeeded && day <= 31; day++) {
    const date = new Date(year, month + 1, day);
    days.push({
      date: date,
      dayNumber: day,
      isCurrentMonth: false,
      isSelected: isSameDay(date, selectedDate.value),
      isToday: isSameDay(date, today),
      isDisabled:
        !props.backdate && (date > today || disabledDates.value.includes(date))
    });
  }

  calendarDays.value = days;
};

const isSameDay = (date1: Date, date2: Date) => {
  if (!date1 || !date2) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const selectDate = (date) => {
  selectedDate.value = new Date(date);
  generateCalendar();
  if (props.backdate) return;
  getDateTimeSlots({
    date: selectedDate.value,
    serviceCategoryId: props.appointment.service_category.id,
    durationMinutes: props.appointment.estimated_duration_minutes,
    userId: props.appointment.technician?.user_id,
    userIds: techUserIds.value,
    zipcode: props.appointment.address.zip_code
  });
};

const previousMonth = () => {
  currentMonthDate.value = new Date(
    currentMonthDate.value.getFullYear(),
    currentMonthDate.value.getMonth() - 1,
    1
  );
  generateCalendar();
};

const nextMonth = () => {
  currentMonthDate.value = new Date(
    currentMonthDate.value.getFullYear(),
    currentMonthDate.value.getMonth() + 1,
    1
  );
  generateCalendar();
};

const handleSave = () => {
  if (props.backdate) {
    if (!selectedDate.value) return;
    const scheduledTime = moment
      .tz(
        `${moment(selectedDate.value).format("MM/DD/YYYY")} ${
          manualTime.value
        }`,
        "MM/DD/YYYY h:mm A",
        currentTenantTimezone
      )
      .toISOString();
    window.tippy?.hideAll();
    emit("request-save", {
      action: "updateAppointmentEvent",
      payload: {
        appointmentId: props.appointment.id,
        scheduled_time: scheduledTime,
        skip_notifications: true,
        sync_technician_times: syncTechTimes.value
      }
    });
    return;
  }
  if (!selectedTimeSlot.value) return;
  window.tippy?.hideAll();
  emit("request-save", {
    action: "rescheduleAppointmentEvent",
    payload: {
      appointmentId: props.appointment.id,
      arrival_window_start: selectedTimeSlot.value.arrival_window_start,
      arrival_window_end: selectedTimeSlot.value.arrival_window_end,
      scheduled_time: selectedTimeSlot.value.scheduled_time,
      // Only present when the caller opted into the toggle: an always-present
      // key would route every desktop reschedule straight past
      // RescheduleNotifyModal.
      ...(props.showNotifyToggle
        ? { skip_notifications: !notifyCustomer.value }
        : {})
    }
  });
};

watch(currentMonthDate, (val, oldVal) => {
  if (props.backdate) return;
  if (val !== oldVal) {
    timeSlots.value = [];
    selectedDate.value = null;
    selectedTimeSlot.value = null;
    getMonthAvailability({
      month: currentMonthDate.value,
      serviceCategoryId: props.appointment.service_category.id,
      durationMinutes: props.appointment.estimated_duration_minutes,
      userId: props.appointment.technician?.user_id,
      userIds: techUserIds.value,
      zipcode: props.appointment.address.zip_code
    });
  }
});

onMounted(() => {
  if (props.backdate) {
    const scheduled = new Date(props.appointment.scheduled_time);
    currentMonthDate.value = scheduled;
    selectedDate.value = scheduled;
    generateCalendar();
    return;
  }
  currentMonthDate.value = new Date();
  selectedDate.value = new Date();
  generateCalendar();
  getMonthAvailability({
    month: currentMonthDate.value,
    serviceCategoryId: props.appointment.service_category.id,
    durationMinutes: props.appointment.estimated_duration_minutes,
    userId: props.appointment.technician?.user_id,
    userIds: techUserIds.value,
    zipcode: props.appointment.address.zip_code
  });
  getDateTimeSlots({
    date: selectedDate.value,
    serviceCategoryId: props.appointment.service_category.id,
    durationMinutes: props.appointment.estimated_duration_minutes,
    userId: props.appointment.technician?.user_id,
    userIds: techUserIds.value,
    zipcode: props.appointment.address.zip_code
  });
});
</script>
